import React, { useState, useRef } from 'react';
import { apiClient } from '../../api/client';
import { Button } from '../common/Button';

interface PlayerCardUploadProps {
  playerId: string;
  playerName: string;
  hasCard: boolean;
  onUploadSuccess: () => void;
}

export function PlayerCardUpload({ playerId, playerName, hasCard, onUploadSuccess }: PlayerCardUploadProps) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (file: File) => {
    setError('');

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (PNG or JPG)');
      return;
    }

    // Validate file size (1MB limit)
    const maxSize = 1 * 1024 * 1024; // 1MB
    if (file.size > maxSize) {
      setError('Image must be less than 1MB. Please compress your image.');
      return;
    }

    // Convert to Base64
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result as string;
      setPreview(base64);

      // Upload to server
      try {
        setUploading(true);
        await apiClient.uploadPlayerCard(playerId, base64);
        onUploadSuccess();
        setError('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload card');
        setPreview(null);
      } finally {
        setUploading(false);
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
    };

    reader.readAsDataURL(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Delete card for ${playerName}?`)) return;

    try {
      setUploading(true);
      await apiClient.deletePlayerCard(playerId);
      setPreview(null);
      onUploadSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete card');
    } finally {
      setUploading(false);
    }
  };

  const viewCard = async () => {
    try {
      const response = await apiClient.getPlayerCard(playerId);
      setPreview(response.image);
    } catch (err) {
      setError('Failed to load card');
    }
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          dragActive
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : hasCard
            ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInput}
          className="hidden"
          disabled={uploading}
        />

        {uploading ? (
          <div>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-3"></div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Uploading...</p>
          </div>
        ) : hasCard ? (
          <div>
            <div className="text-green-600 dark:text-green-400 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Card Uploaded</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Click or drag to replace</p>
            <div className="flex justify-center gap-2">
              <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); viewCard(); }}>
                View Card
              </Button>
              <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleDelete(); }}>
                Delete
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="text-gray-400 mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">Upload FIFA Card</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Click or drag image here</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PNG or JPG, max 1MB</p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-400 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4" onClick={() => setPreview(null)}>
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{playerName}'s Card</h3>
              <button onClick={() => setPreview(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <img src={preview} alt={`${playerName} card`} className="w-full rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
}
